import { MouseEventHandler, TouchEventHandler, useCallback, useMemo, useState } from 'react'
import { Trans } from 'react-i18next'

import { getShortlistNextSortOrder } from '@modules/shortlist/constants'
import { TableHeadCellProps } from '@modules/shortlist/types'
import { Typography } from '@mui/material'
import { useScreenDimensions } from '@shared/hooks/useScreenDimensions'

import SortArrow from './SortArrow'
import { ResizeIcon, ResizeWrapper, StyledTableCell, TableCellInnerContent, TableHeadCellInner } from './index.styles'

const TableHeadCell = ({
  fieldName: defaultFieldName,
  restCellProps = {},
  cellComponent,
  columnTitle,
  isSortable,
  isSortActive,
  sortOrder,
  updateConfig,
  onSortChange,
  optionalColumnsConfig,
  customField,
  tableColumn,
}: TableHeadCellProps) => {
  const fieldName = defaultFieldName === 'profileTitle' ? 'title' : defaultFieldName
  const { isDesktop } = useScreenDimensions()
  const [isHovered, setIsHovered] = useState(false)
  const nextSortOrder = useMemo(
    () => getShortlistNextSortOrder(isSortActive, sortOrder, fieldName),
    [isSortActive, sortOrder, fieldName],
  )

  const onResizeStop = useCallback(() => {
    if (optionalColumnsConfig) {
      updateConfig({
        ...optionalColumnsConfig,
        [customField?.id || fieldName]: {
          visible: true,
          ...optionalColumnsConfig[fieldName],
          width: Math.round(tableColumn.getSize()),
        },
      })

      document.removeEventListener('mouseup', onResizeStop)
    }
  }, [fieldName, updateConfig, customField?.id, optionalColumnsConfig, tableColumn])

  const handleChangeSort = useCallback(() => {
    if (isSortable) {
      onSortChange?.({ fieldName: defaultFieldName, sortOrder: nextSortOrder })
    }
  }, [isSortable, onSortChange, nextSortOrder, defaultFieldName])

  const handleDragStart: MouseEventHandler<HTMLDivElement> = e => {
    e.preventDefault()
    document.addEventListener('mouseup', onResizeStop)

    tableColumn.getResizeHandler()(e)
  }

  const handleTouchDragStart: TouchEventHandler<HTMLDivElement> = e => {
    e.preventDefault()
    document.addEventListener('mouseup', onResizeStop)

    tableColumn.getResizeHandler()(e)
  }

  return (
    <StyledTableCell
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...restCellProps}
      $header
      key={`column-head-${fieldName}`}
      data-testid={`head-${fieldName}_testid`}
      $cellWidth={tableColumn.getSize()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <TableHeadCellInner>
        {cellComponent ? (
          <TableCellInnerContent onClick={handleChangeSort}>{cellComponent(customField)}</TableCellInnerContent>
        ) : (
          <TableCellInnerContent onClick={handleChangeSort}>
            <Typography variant="h3">{columnTitle && <Trans>{columnTitle}</Trans>}</Typography>
            {isSortable && (
              <SortArrow
                isActive={isSortActive}
                order={isSortActive ? sortOrder : null}
                isHovered={isHovered}
                fieldName={fieldName}
              />
            )}
          </TableCellInnerContent>
        )}
        {fieldName === 'title' && isDesktop ? (
          <ResizeWrapper onMouseDown={handleDragStart} onTouchStart={handleTouchDragStart} onTouchEnd={onResizeStop}>
            <ResizeIcon />
          </ResizeWrapper>
        ) : null}
      </TableHeadCellInner>
    </StyledTableCell>
  )
}

export default TableHeadCell
